import React, {
  useState, useEffect, useRef, useCallback,
  forwardRef, useImperativeHandle
} from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './SpeedMathBoard.module.css';
import successSound from '../../assets/sounds/success.mp3';
import failSound from '../../assets/sounds/fail.mp3';

// 모듈 스코프에서 한 번만 생성하여 매 렌더 누수 방지
const successAudio = new Audio(successSound);
const failAudio = new Audio(failSound);
successAudio.volume = 0.5;
failAudio.volume = 0.5;

const GAME_DURATION = 60;
const WRONG_PENALTY = 3;

const randInt = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

const generate3TermProblem = () => {
  const variant = randInt(0, 3);
  if (variant === 0) {
    // a + b − c (양수 결과 보장)
    const a = randInt(5, 30);
    const b = randInt(5, 30);
    const c = randInt(1, a + b - 1);
    return { text: `${a} + ${b} − ${c} = ?`, answer: a + b - c };
  } else if (variant === 1) {
    // a + b + c
    const a = randInt(2, 20);
    const b = randInt(2, 20);
    const c = randInt(2, 20);
    return { text: `${a} + ${b} + ${c} = ?`, answer: a + b + c };
  } else if (variant === 2) {
    // a × b + c (연산 우선순위 응용)
    const a = randInt(2, 9);
    const b = randInt(2, 9);
    const c = randInt(1, 20);
    return { text: `${a} × ${b} + ${c} = ?`, answer: a * b + c };
  } else {
    // a × b − c (양수 결과 보장)
    const a = randInt(2, 9);
    const b = randInt(2, 9);
    const c = randInt(1, a * b - 1);
    return { text: `${a} × ${b} − ${c} = ?`, answer: a * b - c };
  }
};

const generateProblem = (mode) => {
  let op = mode;
  if (mode === 'mix') {
    // 30% 확률로 3항 문제, 70% 단일 연산
    if (Math.random() < 0.3) {
      return generate3TermProblem();
    }
    op = ['add', 'sub', 'mul', 'div'][randInt(0, 3)];
  }
  switch (op) {
    case 'add': {
      const a = randInt(1, 50);
      const b = randInt(1, 50);
      return { text: `${a} + ${b} = ?`, answer: a + b };
    }
    case 'sub': {
      const a = randInt(1, 50);
      const b = randInt(1, a);
      return { text: `${a} − ${b} = ?`, answer: a - b };
    }
    case 'mul': {
      const a = randInt(2, 12);
      const b = randInt(2, 12);
      return { text: `${a} × ${b} = ?`, answer: a * b };
    }
    case 'div': {
      const divisor = randInt(2, 12);
      const quotient = randInt(2, 12);
      return { text: `${divisor * quotient} ÷ ${divisor} = ?`, answer: quotient };
    }
    default:
      return { text: '1 + 1 = ?', answer: 2 };
  }
};

const generateChoices = (answer) => {
  const set = new Set([answer]);
  const offsets = [-5, -2, -1, 1, 2, 5, -3, 3];
  let attempts = 0;
  while (set.size < 4 && attempts < 50) {
    const candidate = answer + offsets[randInt(0, offsets.length - 1)];
    if (candidate >= 0) set.add(candidate);
    attempts++;
  }
  while (set.size < 4) {
    set.add(randInt(0, Math.max(answer + 10, 30)));
  }
  const arr = Array.from(set);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const SpeedMathBoard = forwardRef((props, ref) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState('mix');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [problem, setProblem] = useState(() => generateProblem('mix'));
  const [choices, setChoices] = useState(() => generateChoices(problem.answer));
  const [isGameOver, setIsGameOver] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isSoundMuted, setIsSoundMuted] = useState(false);

  const gameTimerRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const isSoundMutedRef = useRef(false);

  useEffect(() => {
    isSoundMutedRef.current = isSoundMuted;
  }, [isSoundMuted]);

  const playSound = useCallback((audio) => {
    if (isSoundMutedRef.current) return;
    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
  }, []);

  const nextProblem = useCallback((forMode) => {
    const p = generateProblem(forMode);
    setProblem(p);
    setChoices(generateChoices(p.answer));
  }, []);

  const clearAllTimers = useCallback(() => {
    if (gameTimerRef.current) {
      clearInterval(gameTimerRef.current);
      gameTimerRef.current = null;
    }
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  const startGame = useCallback((forMode) => {
    clearAllTimers();
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setIsGameOver(false);
    setFeedback(null);
    nextProblem(forMode);

    gameTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearAllTimers();
          setIsGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearAllTimers, nextProblem]);

  useEffect(() => {
    startGame(mode);
    return () => clearAllTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    resetGame: () => startGame(mode),
  }), [startGame, mode]);

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setMode(newMode);
    startGame(newMode);
  };

  const handleAnswerClick = (value) => {
    if (isGameOver) return;
    if (value === problem.answer) {
      setScore(s => s + 1);
      setFeedback('correct');
      playSound(successAudio);
    } else {
      setFeedback('wrong');
      playSound(failAudio);
      setTimeLeft(prev => Math.max(0, prev - WRONG_PENALTY));
    }
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => {
      setFeedback(null);
      feedbackTimerRef.current = null;
    }, 150);
    nextProblem(mode);
  };

  return (
    <div className={styles.board}>
      <div className={styles.controlBar}>
        <div className={styles.modeSelector}>
          <span className={styles.modeLabel}>{t('games.speedMath.mode')}</span>
          <select
            value={mode}
            onChange={handleModeChange}
            className={styles.modeDropdown}
          >
            <option value="add">{t('games.speedMath.modes.add')}</option>
            <option value="sub">{t('games.speedMath.modes.sub')}</option>
            <option value="mul">{t('games.speedMath.modes.mul')}</option>
            <option value="mix">{t('games.speedMath.modes.mix')}</option>
          </select>
        </div>
        <button
          className={styles.soundToggle}
          onClick={() => setIsSoundMuted(m => !m)}
          aria-label="sound"
        >
          {isSoundMuted ? '🔇' : '🔊'}
        </button>
      </div>

      <div className={styles.statBar}>
        <div className={styles.statBox}>
          <span className={styles.statLabel}>{t('score')}</span>
          <span className={styles.statValue}>{score}</span>
        </div>
        <div className={`${styles.statBox} ${timeLeft <= 10 ? styles.timeWarning : ''}`}>
          <span className={styles.statLabel}>{t('games.speedMath.timeLeft')}</span>
          <span className={styles.statValue}>{timeLeft}</span>
        </div>
      </div>

      <div
        className={`${styles.problem} ${
          feedback === 'correct' ? styles.problemCorrect : ''
        } ${feedback === 'wrong' ? styles.problemWrong : ''}`}
      >
        {problem.text}
      </div>

      <div className={styles.choices}>
        {choices.map((c, i) => (
          <button
            key={`${problem.text}-${i}-${c}`}
            className={styles.choiceButton}
            onClick={() => handleAnswerClick(c)}
            disabled={isGameOver}
          >
            {c}
          </button>
        ))}
      </div>

      {isGameOver && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t('gameOver')}</h2>
            <p>
              {t('finalScore')}: <strong>{score}</strong>
            </p>
            <div className="modal-buttons">
              <button onClick={() => startGame(mode)}>
                {t('playAgain')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SpeedMathBoard;
